import React, { useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  Collapse,
  Tooltip,
  Menu,
  MenuItem,
  Typography,
  Divider,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import MoveToInboxOutlinedIcon from "@mui/icons-material/MoveToInboxOutlined";
import OutboxOutlinedIcon from "@mui/icons-material/OutboxOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import { HELP_CENTER_URL } from "../config/supportConfig";
import { Link, useLocation } from "react-router-dom";
import AuthService from "../services/authService";

// Ancho único de la sidebar, compartido con el espaciador de DashboardRoutes
// para que el hueco reservado coincida exactamente con el drawer.
export const SIDEBAR_WIDTH_OPEN = 260;
export const SIDEBAR_WIDTH_COLLAPSED = 72;

// El owner ve todo el menú (es la raíz del tenant y las rutas ya lo dejan pasar
// vía canAccessTab); el resto, según sus permisos. Así el menú queda consistente
// con los guards de ruta y no esconde links a páginas que el owner sí puede abrir.
const hasTabPermission = (tab) => AuthService.canAccessTab(tab);

const hasPermission = (tab, control) => {
  return AuthService.isOwner() || AuthService.hasPermission(control, tab);
};

const Sidebar = ({ open, onClose }) => {
  const [expandedMenus, setExpandedMenus] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentMenu, setCurrentMenu] = useState(null);
  const location = useLocation();
  const userId = Number(sessionStorage.getItem("User"));


  const handleMenuToggle = (menuName) => {
    if (open) {
      setExpandedMenus((prev) => ({
        ...prev,
        [menuName]: !prev[menuName],
      }));
    }
  };

  const handleCollapsedMenuClick = (event, menuName) => {
    if (!open) {
      setAnchorEl(event.currentTarget);
      setCurrentMenu(menuName);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentMenu(null);
  };

  const sidebarWidth = open ? SIDEBAR_WIDTH_OPEN : SIDEBAR_WIDTH_COLLAPSED;

  const isActive = (path) => location.pathname === path;
  const isGroupActive = (paths) => paths.some((p) => location.pathname.startsWith(p));

  const activeItemSx = (path) => ({
    borderRadius: "10px",
    mb: 0.5,
    transition: "all 0.15s ease",
    cursor: "pointer",
    justifyContent: open ? "flex-start" : "center",
    // Acento de marca (naranja) en el ítem activo.
    backgroundColor: isActive(path) ? "rgba(242,101,26,0.18)" : "transparent",
    "&:hover": {
      backgroundColor: isActive(path) ? "rgba(242,101,26,0.22)" : "rgba(15,23,42,0.05)",
    },
  });

  const activeGroupSx = (paths) => ({
    borderRadius: "10px",
    mb: 0.5,
    transition: "all 0.15s ease",
    cursor: "pointer",
    justifyContent: open ? "flex-start" : "center",
    backgroundColor: isGroupActive(paths) ? "rgba(242,101,26,0.14)" : "transparent",
    "&:hover": {
      backgroundColor: isGroupActive(paths) ? "rgba(242,101,26,0.18)" : "rgba(15,23,42,0.05)",
    },
  });

  const iconSx = (active = false) => ({
    color: active ? "#F2651A" : "#64748b",
    fontSize: 22,
    transition: "color 0.15s ease",
  });

  const textSx = (active = false) => ({
    color: active ? "#F2651A" : "#64748b",
    fontSize: "0.88rem",
    fontWeight: active ? 600 : 400,
    transition: "all 0.15s ease",
  });

  const subItemSx = (path) => ({
    pl: 4.5,
    borderRadius: "8px",
    mb: 0.3,
    py: 0.6,
    backgroundColor: isActive(path) ? "rgba(242,101,26,0.16)" : "transparent",
    "&:hover": {
      backgroundColor: isActive(path) ? "rgba(242,101,26,0.20)" : "#e2e8f0",
    },
  });

  const renderCollapsedMenu = (menuName, items) => (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl) && currentMenu === menuName}
      onClose={handleMenuClose}
      anchorOrigin={{ vertical: "center", horizontal: "right" }}
      transformOrigin={{ vertical: "center", horizontal: "left" }}
      PaperProps={{
        sx: {
          backgroundColor: "#ffffff",
          color: "#334155",
          ml: 1,
          borderRadius: "10px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
          minWidth: 200,
        },
      }}
    >
      {items.map((item, index) => (
        <MenuItem
          key={index}
          component={Link}
          to={item.to}
          onClick={handleMenuClose}
          sx={{
            color: "#334155",
            borderRadius: "6px",
            mx: 0.5,
            "&:hover": {
              backgroundColor: "rgba(15,23,42,0.05)",
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: "36px" }}>
            <item.icon sx={{ color: "#64748b", fontSize: 18 }} />
          </ListItemIcon>
          <ListItemText
            primary={item.text}
            primaryTypographyProps={{
              sx: { color: "#334155", fontSize: "0.85rem" },
            }}
          />
        </MenuItem>
      ))}
    </Menu>
  );

  return (
    <>
      <Drawer
        variant="permanent"
        sx={{
          width: sidebarWidth,
          flexShrink: 0,
          transition: "width 0.25s ease",
          [`& .MuiDrawer-paper`]: {
            width: sidebarWidth,
            boxSizing: "border-box",
            border: "none",
            borderRight: "1px solid #e2e8f0",
            backgroundColor: "#ffffff",
            overflowX: "hidden",
            top: "64px",
            height: "calc(100% - 64px)",
            transition: "width 0.25s ease",
          },
        }}
      >
        <Box sx={{ height: 8 }} />

        {/* Sección principal */}
        {open && (
          <Typography
            variant="overline"
            sx={{
              color: "#94a3b8",
              fontSize: "0.65rem",
              fontWeight: 600,
              letterSpacing: "0.08em",
              px: 2.5,
              pt: 2,
              pb: 0.5,
            }}
          >
            Principal
          </Typography>
        )}

        <List sx={{ width: "100%", px: 1, pt: open ? 0 : 1.5 }}>
          {/* Inicio */}
          <Tooltip title={open ? "" : "Inicio"} placement="right" arrow>
            <ListItem
              component={Link}
              to="/home"
              sx={activeItemSx("/home")}
            >
              <ListItemIcon
                sx={{
                  minWidth: open ? "40px" : "auto",
                  display: "flex",
                  justifyContent: open ? "flex-start" : "center",
                }}
              >
                <HomeOutlinedIcon sx={iconSx(isActive("/home"))} />
              </ListItemIcon>
              {open && (
                <ListItemText
                  primary="Inicio"
                  primaryTypographyProps={{ sx: textSx(isActive("/home")) }}
                />
              )}
            </ListItem>
          </Tooltip>

          {/* Inventario */}
          {hasTabPermission("product") && (
            <Tooltip title={open ? "" : "Inventario"} placement="right" arrow>
              <ListItem
                component={Link}
                to="/inventory"
                sx={activeItemSx("/inventory")}
              >
                <ListItemIcon
                  sx={{
                    minWidth: open ? "40px" : "auto",
                    display: "flex",
                    justifyContent: open ? "flex-start" : "center",
                  }}
                >
                  <InventoryOutlinedIcon sx={iconSx(isActive("/inventory"))} />
                </ListItemIcon>
                {open && (
                  <ListItemText
                    primary="Inventario"
                    primaryTypographyProps={{ sx: textSx(isActive("/inventory")) }}
                  />
                )}
              </ListItem>
            </Tooltip>
          )}
        </List>

        {/* Sección Operaciones */}
        {(hasTabPermission("movement_in") || hasTabPermission("movement_out")) && (
          <>
            {open && (
              <Typography
                variant="overline"
                sx={{
                  color: "#94a3b8",
                  fontSize: "0.65rem",
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  px: 2.5,
                  pt: 1.5,
                  pb: 0.5,
                }}
              >
                Operaciones
              </Typography>
            )}

            {!open && (
              <Divider sx={{ borderColor: "#e2e8f0", mx: 1.5, my: 1 }} />
            )}

            <List sx={{ width: "100%", px: 1, pt: 0 }}>
              {/* Órdenes de Ingreso */}
              {hasTabPermission("movement_in") && (
                <>
                  <Tooltip title={open ? "" : "Ingresos"} placement="right" arrow>
                    <ListItem
                      onClick={(e) => {
                        if (open) {
                          handleMenuToggle("ingreso");
                        } else {
                          handleCollapsedMenuClick(e, "ingreso");
                        }
                      }}
                      sx={activeGroupSx(["/create-ingress", "/ingress-list"])}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: open ? "40px" : "auto",
                          display: "flex",
                          justifyContent: open ? "flex-start" : "center",
                        }}
                      >
                        <MoveToInboxOutlinedIcon
                          sx={iconSx(isGroupActive(["/create-ingress", "/ingress-list"]))}
                        />
                      </ListItemIcon>
                      {open && (
                        <>
                          <ListItemText
                            primary="Ingresos"
                            primaryTypographyProps={{
                              sx: textSx(isGroupActive(["/create-ingress", "/ingress-list"])),
                            }}
                          />
                          {expandedMenus.ingreso ? (
                            <ExpandLess sx={{ color: "#94a3b8", fontSize: 18 }} />
                          ) : (
                            <ExpandMore sx={{ color: "#94a3b8", fontSize: 18 }} />
                          )}
                        </>
                      )}
                    </ListItem>
                  </Tooltip>

                  {open && (
                    <Collapse in={expandedMenus.ingreso} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {hasPermission("movement_in", "create") && (
                          <ListItem
                            component={Link}
                            to="/create-ingress"
                            sx={subItemSx("/create-ingress")}
                          >
                            <ListItemIcon sx={{ minWidth: "32px" }}>
                              <NoteAddOutlinedIcon
                                sx={{ color: "#94a3b8", fontSize: 18 }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary="Nuevo ingreso"
                              primaryTypographyProps={{
                                sx: {
                                  color: isActive("/create-ingress") ? "#F2651A" : "#64748b",
                                  fontSize: "0.82rem",
                                  fontWeight: isActive("/create-ingress") ? 500 : 400,
                                },
                              }}
                            />
                          </ListItem>
                        )}
                        {hasPermission("movement_in", "read") && (
                          <ListItem
                            component={Link}
                            to="/ingress-list"
                            sx={subItemSx("/ingress-list")}
                          >
                            <ListItemIcon sx={{ minWidth: "32px" }}>
                              <ListAltOutlinedIcon
                                sx={{ color: "#94a3b8", fontSize: 18 }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary="Todos los ingresos"
                              primaryTypographyProps={{
                                sx: {
                                  color: isActive("/ingress-list") ? "#F2651A" : "#64748b",
                                  fontSize: "0.82rem",
                                  fontWeight: isActive("/ingress-list") ? 500 : 400,
                                },
                              }}
                            />
                          </ListItem>
                        )}
                      </List>
                    </Collapse>
                  )}

                  {renderCollapsedMenu("ingreso", [
                    ...(hasPermission("movement_in", "create")
                      ? [{ to: "/create-ingress", icon: NoteAddOutlinedIcon, text: "Nuevo ingreso" }]
                      : []),
                    ...(hasPermission("movement_in", "read")
                      ? [{ to: "/ingress-list", icon: ListAltOutlinedIcon, text: "Todos los ingresos" }]
                      : []),
                  ])}
                </>
              )}

              {/* Órdenes de Egreso */}
              {hasTabPermission("movement_out") && (
                <>
                  <Tooltip title={open ? "" : "Egresos"} placement="right" arrow>
                    <ListItem
                      onClick={(e) => {
                        if (open) {
                          handleMenuToggle("egreso");
                        } else {
                          handleCollapsedMenuClick(e, "egreso");
                        }
                      }}
                      sx={activeGroupSx(["/create-egress", "/egress-list"])}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: open ? "40px" : "auto",
                          display: "flex",
                          justifyContent: open ? "flex-start" : "center",
                        }}
                      >
                        <OutboxOutlinedIcon
                          sx={iconSx(isGroupActive(["/create-egress", "/egress-list"]))}
                        />
                      </ListItemIcon>
                      {open && (
                        <>
                          <ListItemText
                            primary="Egresos"
                            primaryTypographyProps={{
                              sx: textSx(isGroupActive(["/create-egress", "/egress-list"])),
                            }}
                          />
                          {expandedMenus.egreso ? (
                            <ExpandLess sx={{ color: "#94a3b8", fontSize: 18 }} />
                          ) : (
                            <ExpandMore sx={{ color: "#94a3b8", fontSize: 18 }} />
                          )}
                        </>
                      )}
                    </ListItem>
                  </Tooltip>

                  {open && (
                    <Collapse in={expandedMenus.egreso} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {hasPermission("movement_out", "create") && (
                          <ListItem
                            component={Link}
                            to="/create-egress"
                            sx={subItemSx("/create-egress")}
                          >
                            <ListItemIcon sx={{ minWidth: "32px" }}>
                              <NoteAddOutlinedIcon
                                sx={{ color: "#94a3b8", fontSize: 18 }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary="Nuevo egreso"
                              primaryTypographyProps={{
                                sx: {
                                  color: isActive("/create-egress") ? "#F2651A" : "#64748b",
                                  fontSize: "0.82rem",
                                  fontWeight: isActive("/create-egress") ? 500 : 400,
                                },
                              }}
                            />
                          </ListItem>
                        )}
                        {hasPermission("movement_out", "read") && (
                          <ListItem
                            component={Link}
                            to="/egress-list"
                            sx={subItemSx("/egress-list")}
                          >
                            <ListItemIcon sx={{ minWidth: "32px" }}>
                              <ListAltOutlinedIcon
                                sx={{ color: "#94a3b8", fontSize: 18 }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary="Todos los egresos"
                              primaryTypographyProps={{
                                sx: {
                                  color: isActive("/egress-list") ? "#F2651A" : "#64748b",
                                  fontSize: "0.82rem",
                                  fontWeight: isActive("/egress-list") ? 500 : 400,
                                },
                              }}
                            />
                          </ListItem>
                        )}
                      </List>
                    </Collapse>
                  )}

                  {renderCollapsedMenu("egreso", [
                    ...(hasPermission("movement_out", "create")
                      ? [{ to: "/create-egress", icon: NoteAddOutlinedIcon, text: "Nuevo egreso" }]
                      : []),
                    ...(hasPermission("movement_out", "read")
                      ? [{ to: "/egress-list", icon: ListAltOutlinedIcon, text: "Todos los egresos" }]
                      : []),
                  ])}
                </>
              )}
            </List>
          </>
        )}

        {/* Sección Ayuda — Centro de ayuda (knowledge base de Tawk). Oculta hasta
            configurar HELP_CENTER_URL en config/supportConfig.js. */}
        {HELP_CENTER_URL && (
          <>
            {open && (
              <Typography
                variant="overline"
                sx={{
                  color: "#94a3b8",
                  fontSize: "0.65rem",
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  px: 2.5,
                  pt: 1.5,
                  pb: 0.5,
                }}
              >
                Ayuda
              </Typography>
            )}
            {!open && (
              <Divider sx={{ borderColor: "#e2e8f0", mx: 1.5, my: 1 }} />
            )}
          <List sx={{ width: "100%", px: 1, pt: 0 }}>
            <Tooltip title={open ? "" : "Centro de ayuda"} placement="right" arrow>
              <ListItem
                component="a"
                href={HELP_CENTER_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={activeItemSx("/__centro-ayuda__")}
              >
                <ListItemIcon
                  sx={{
                    minWidth: open ? "40px" : "auto",
                    display: "flex",
                    justifyContent: open ? "flex-start" : "center",
                  }}
                >
                  <HelpOutlineOutlinedIcon sx={iconSx(false)} />
                </ListItemIcon>
                {open && (
                  <ListItemText
                    primary="Centro de ayuda"
                    primaryTypographyProps={{ sx: textSx(false) }}
                  />
                )}
              </ListItem>
            </Tooltip>
          </List>
          </>
        )}

        {userId === 1 && (
          <>
            {/* Sección Administración */}
            {open && (
              <Typography
                variant="overline"
                sx={{
                  color: "#94a3b8",
                  fontSize: "0.65rem",
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  px: 2.5,
                  pt: 1.5,
                  pb: 0.5,
                }}
              >
                Administración
              </Typography>
            )}
            {!open && (
              <Divider sx={{ borderColor: "#e2e8f0", mx: 1.5, my: 1 }} />
            )}

            <List sx={{ width: "100%", px: 1, pt: 0 }}>
              <Tooltip title={open ? "" : "Usuarios"} placement="right" arrow>
                <ListItem
                  component={Link}
                  to="/users-panel"
                  sx={activeItemSx("/users-panel")}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: open ? "40px" : "auto",
                      display: "flex",
                      justifyContent: open ? "flex-start" : "center",
                    }}
                  >
                    <PeopleOutlinedIcon sx={iconSx(isActive("/users-panel"))} />
                  </ListItemIcon>
                  {open && (
                    <ListItemText
                      primary="Usuarios"
                      primaryTypographyProps={{ sx: textSx(isActive("/users-panel")) }}
                    />
                  )}
                </ListItem>
              </Tooltip>
            </List>
          </>
        )}

      </Drawer>
    </>
  );
};

export default Sidebar;